import { useState, useRef, useEffect, useMemo } from 'react';
import SelectField from '../common/SelectField.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import DeliveryOtpModal from '../common/DeliveryOtpModal.js';
import shipmentQrStatusService from '../../services/shipmentQrStatusService.js';
import shipmentCrossingService from '../../services/shipmentCrossingService.js';
import CrossingVehicleSelect from '../shipment-crossing/CrossingVehicleSelect.js';
import { prepareScanImageFile, prepareQrScanImageFile, isAcceptedScanFile } from '../../utils/normalizeCameraImage.js';
import { getQrBatchStatusOptions, isCreditClientShipment, isShipmentFullyPaid, normalizeQrResolveResponse, getQrTransitionEligibility, getEligibleShipmentsForQrStatus, requiresDeliveryCollection } from '../../utils/shipmentPayment.js';
import { isOtpRequiredResponse, normalizeOtpPending } from '../../utils/deliveryOtp.js';
import { Camera, QrCode, CheckCircle, Upload, FileText, X, Shuffle, CalendarCheck } from 'lucide-react';
import LiveQrScanner, { supportsLiveQrScan } from '../common/LiveQrScanner.js';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.js';
import { getCurrentDevicePosition } from '../../utils/deviceLocation.js';
import {
  hasShipmentCrossingAccess,
  canScanQrUpdateStatus,
  canScanQrAttendance,
} from '../../utils/networkMode.js';
import { parseHubAttendanceQr } from '../../utils/parseHubAttendanceQr.js';
import { parseShipmentIdFromQrData } from '../../utils/parseQrShipmentId.js';
import attendanceService from '../../services/AttendanceService.js';
import { ensureCameraPermission } from '../../utils/devicePermissions.js';
import CapturePhotoField from '../common/CapturePhotoField.js';

const isPdfFile = (file) =>
  file?.type === 'application/pdf' || (file?.name || '').toLowerCase().endsWith('.pdf');

const ScanQrUpdateStatusModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const accountantAttendanceOnly = user?.role === 'Accountant';
  const crossingAllowed = !accountantAttendanceOnly && hasShipmentCrossingAccess(user);
  const statusAllowed = !accountantAttendanceOnly && canScanQrUpdateStatus(user);
  const attendanceAllowed = canScanQrAttendance(user);
  const defaultMode = attendanceAllowed ? 'attendance' : (statusAllowed ? 'status' : 'crossing');
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);
  // 'attendance' | 'status' | 'crossing'
  const [mode, setMode] = useState(defaultMode);
  const [crossingShipments, setCrossingShipments] = useState([]);
  const [crossingFailed, setCrossingFailed] = useState([]);
  const [crossingVehicle, setCrossingVehicle] = useState(null);
  const [crossingHubCity, setCrossingHubCity] = useState(null);
  const [crossingSubmitting, setCrossingSubmitting] = useState(false);
  const [attendanceSubmitting, setAttendanceSubmitting] = useState(false);
  const [attendanceMessage, setAttendanceMessage] = useState('');

  const [scanFile, setScanFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [resolvedShipments, setResolvedShipments] = useState([]);
  const [resolveFailed, setResolveFailed] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [todayMlSheets, setTodayMlSheets] = useState([]);
  const [mlSheetsLoading, setMlSheetsLoading] = useState(false);
  const [selectedMlSheetId, setSelectedMlSheetId] = useState('');
  const [remarks, setRemarks] = useState('');
  const [deliveryReceiver, setDeliveryReceiver] = useState('');
  const [deliveryContact, setDeliveryContact] = useState('');
  const [deliveryImage, setDeliveryImage] = useState(null);
  const [deliveryImagePreview, setDeliveryImagePreview] = useState(null);
  const [deliveryGovIdImage, setDeliveryGovIdImage] = useState(null);
  const [deliveryGovIdPreview, setDeliveryGovIdPreview] = useState(null);
  const [otpPending, setOtpPending] = useState([]);
  const [liveScannerOpen, setLiveScannerOpen] = useState(false);
  const [liveResolving, setLiveResolving] = useState(false);
  const liveInFlightRef = useRef(false);

  const resetPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const resetScanResults = () => {
    resetPreview();
    setScanFile(null);
    setResolvedShipments([]);
    setResolveFailed([]);
    setSelectedStatus('');
    setSelectedMlSheetId('');
    setTodayMlSheets([]);
    setCrossingShipments([]);
    setCrossingFailed([]);
    setCrossingVehicle(null);
    setCrossingHubCity(null);
    if (cameraRef.current) cameraRef.current.value = '';
    if (uploadRef.current) uploadRef.current.value = '';
  };

  const resetState = () => {
    resetScanResults();
    setLiveScannerOpen(false);
    setLiveResolving(false);
    liveInFlightRef.current = false;
    setMode(defaultMode);
    setRemarks('');
    setAttendanceMessage('');
    setDeliveryReceiver('');
    setDeliveryContact('');
    setDeliveryImage(null);
    setDeliveryImagePreview(null);
    setDeliveryGovIdImage(null);
    setDeliveryGovIdPreview(null);
    setOtpPending([]);
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === mode || updating || resolving || crossingSubmitting || attendanceSubmitting) return;
    resetScanResults();
    setRemarks('');
    setAttendanceMessage('');
    setMode(nextMode);
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const noticeShipments = useMemo(
    () => resolvedShipments.filter((shipment) => shipment.notice_only),
    [resolvedShipments],
  );
  const actionableShipments = useMemo(
    () => resolvedShipments.filter((shipment) => !shipment.notice_only),
    [resolvedShipments],
  );
  const statusConfig = useMemo(
    () => getQrBatchStatusOptions(actionableShipments),
    [actionableShipments],
  );
  const allowedStatuses = statusConfig.options;
  const isBatch = resolvedShipments.length > 1;

  const { eligible: eligibleShipments, skipped: skippedShipments } = useMemo(
    () => getEligibleShipmentsForQrStatus(actionableShipments, selectedStatus),
    [actionableShipments, selectedStatus],
  );

  useEffect(() => {
    if (!resolvedShipments.length) {
      setSelectedStatus('');
      return;
    }
    if (statusConfig.autoSelect) {
      setSelectedStatus(statusConfig.autoSelect);
      return;
    }
    if (allowedStatuses.length > 1) {
      setSelectedStatus('');
      return;
    }
    if (allowedStatuses.length === 1) {
      setSelectedStatus(allowedStatuses[0]);
    }
  }, [resolvedShipments, statusConfig.autoSelect, allowedStatuses]);

  useEffect(() => {
    if (selectedStatus !== 'Dispatched') {
      setTodayMlSheets([]);
      setSelectedMlSheetId('');
      return undefined;
    }
    let cancelled = false;
    setMlSheetsLoading(true);
    shipmentQrStatusService.listTodayMlSheets()
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data || [];
        setTodayMlSheets(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setTodayMlSheets([]);
      })
      .finally(() => {
        if (!cancelled) setMlSheetsLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedStatus]);

  const handleClose = () => {
    if (updating || resolving) return;
    resetState();
    onClose?.();
  };

  const openCamera = async () => {
    if (resolving || updating || liveResolving || attendanceSubmitting) return;
    if (supportsLiveQrScan()) {
      const allowed = await ensureCameraPermission();
      if (!allowed) {
        Alert.error('Camera permission denied. Enable camera access in app settings.');
        return;
      }
      setLiveScannerOpen(true);
      return;
    }
    cameraRef.current?.click();
  };

  const handleFileSelect = async (e, { fromCamera = false } = {}) => {
    const rawFile = e.target.files?.[0];
    if (!rawFile) return;

    if (!isAcceptedScanFile(rawFile) && !fromCamera) {
      Alert.error('Please upload a PDF or image file (PNG, JPG, GIF, WEBP)');
      return;
    }

    try {
      setResolving(true);
      const file = await prepareQrScanImageFile(rawFile, { fromCamera });
      resetPreview();
      setScanFile(file);
      if (!isPdfFile(file)) {
        setPreviewUrl(URL.createObjectURL(file));
      }
      setResolvedShipments([]);
      setResolveFailed([]);
      setSelectedStatus('');
      setCrossingShipments([]);
      setCrossingFailed([]);
      await resolveScan(file);
    } catch (err) {
      console.error('Scan file prepare failed:', err);
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error;
      if (status === 502 || status === 504 || err.code === 'ERR_NETWORK') {
        Alert.error(
          serverMsg ||
            'Upload failed (server timeout or network). Use Wi‑Fi, move closer to the QR, and try again.',
        );
      } else {
        Alert.error(
          serverMsg ||
            (fromCamera
              ? 'Could not process camera photo. Hold steady, ensure the QR is clear, and try again.'
              : 'Failed to process file. Try another image or PDF.'),
        );
      }
    } finally {
      setResolving(false);
      if (cameraRef.current) cameraRef.current.value = '';
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  const getCurrentPosition = () => getCurrentDevicePosition();

  const markAttendanceFromQr = async (qrData) => {
    const hubId = parseHubAttendanceQr(qrData);
    if (!hubId) {
      Alert.error('This is not a location hub attendance QR. Switch tab to update status or crossing.');
      return;
    }
    setAttendanceSubmitting(true);
    try {
      Alert.info('Checking your location...');
      const coords = await getCurrentPosition();
      const response = await attendanceService.qrLoginAttendance(
        qrData,
        coords.latitude,
        coords.longitude,
      );
      const message = response.message || `Attendance marked at hub ${hubId}`;
      setAttendanceMessage(message);
      Alert.success(message);
      if (onSuccess) onSuccess(response);
    } catch (err) {
      Alert.error(err.response?.data?.error || err.message || 'Failed to mark attendance');
    } finally {
      setAttendanceSubmitting(false);
    }
  };

  const resolveScan = async (file) => {
    try {
      if (mode === 'attendance') {
        Alert.error('Use live camera scan for the hub attendance QR.');
        return;
      }
      if (mode === 'crossing') {
        const response = await shipmentCrossingService.resolveFromFile(file);
        const data = response?.data || {};
        const shipments = data.shipments || [];
        if (!shipments.length) {
          throw new Error(response?.error || 'No shipment found in scan');
        }
        setCrossingShipments(shipments);
        setCrossingFailed(data.failed || []);
        setCrossingHubCity(data.crossing_city || null);
        const noticed = shipments.filter((shipment) => shipment.notice_only);
        if (noticed.length === shipments.length) {
          Alert.success(noticed[0].notice_message || 'Scan recorded. Crossing was not changed.');
        } else if ((data.failed || []).length > 0) {
          Alert.error(`${data.failed.length} shipment(s) could not be loaded from scan`);
        }
        return;
      }
      const response = await shipmentQrStatusService.resolveFromQr(file);
      const { shipments, failed } = normalizeQrResolveResponse(response);
      if (!shipments.length) {
        throw new Error(response?.error || 'No shipment found in scan');
      }
      setResolvedShipments(shipments);
      setResolveFailed(failed);
      const noticed = shipments.filter((shipment) => shipment.notice_only);
      if (noticed.length === shipments.length) {
        Alert.success(noticed[0].notice_message || 'Scan recorded. Status was not changed.');
      } else if (failed.length > 0) {
        Alert.error(`${failed.length} shipment(s) could not be loaded from scan`);
      }
    } catch (err) {
      const status = err.response?.status;
      const serverMsg = err.response?.data?.error;
      if (status === 502 || status === 504 || err.code === 'ERR_NETWORK') {
        Alert.error(
          serverMsg ||
            'Could not reach the server. Check your network connection and try a smaller/clearer photo.',
        );
      } else {
        Alert.error(serverMsg || err.message || 'Could not read QR from file');
      }
      setResolvedShipments([]);
      setResolveFailed([]);
      setCrossingShipments([]);
      setCrossingFailed([]);
    } finally {
      setResolving(false);
    }
  };

  const mergeCrossingShipments = (shipments, failed = [], crossingCity) => {
    setCrossingShipments((prev) => {
      const map = new Map(prev.map((s) => [s.shipment_id, s]));
      shipments.forEach((s) => map.set(s.shipment_id, s));
      return Array.from(map.values());
    });
    if (crossingCity) setCrossingHubCity(crossingCity);
    if (failed.length) {
      setCrossingFailed((prev) => {
        const map = new Map(prev.map((f) => [f.shipment_id, f]));
        failed.forEach((f) => map.set(f.shipment_id, f));
        return Array.from(map.values());
      });
    }
  };

  const mergeResolvedShipments = (shipments, failed = []) => {
    setResolvedShipments((prev) => {
      const map = new Map(prev.map((s) => [s.shipment_id, s]));
      shipments.forEach((s) => map.set(s.shipment_id, s));
      return Array.from(map.values());
    });
    if (failed.length) {
      setResolveFailed((prev) => {
        const map = new Map(prev.map((f) => [f.shipment_id, f]));
        failed.forEach((f) => map.set(f.shipment_id, f));
        return Array.from(map.values());
      });
    }
  };

  const handleLiveQrScan = async ({ qrData, shipmentId, imageFile }) => {
    if (liveInFlightRef.current || liveResolving || attendanceSubmitting) return;
    liveInFlightRef.current = true;
    try {
      setLiveResolving(true);
      const hubId = parseHubAttendanceQr(qrData);
      if (hubId) {
        if (!attendanceAllowed) {
          Alert.error('QR attendance is not enabled for this company.');
          return;
        }
        if (mode !== 'attendance') setMode('attendance');
        await markAttendanceFromQr(qrData);
        return;
      }

      const resolvedShipmentId = shipmentId || parseShipmentIdFromQrData(qrData);
      let targetMode = mode;
      if (mode === 'attendance') {
        targetMode = statusAllowed ? 'status' : (crossingAllowed ? 'crossing' : '');
        if (!targetMode) {
          Alert.error('This is not a location hub attendance QR. Switch tab to update status or crossing.');
          return;
        }
        setMode(targetMode);
      }

      const applyCrossing = (response) => {
        const data = response?.data || response || {};
        const shipments = data.shipments || [];
        if (!shipments.length) {
          throw new Error(response?.error || data?.error || 'Shipment not found or access denied');
        }
        mergeCrossingShipments(shipments, data.failed || [], data.crossing_city);
        if (shipments.every((shipment) => shipment.notice_only)) {
          Alert.success(shipments[0].notice_message || 'Scan recorded. Crossing was not changed.');
        } else {
          Alert.success(`Scanned ${shipments[0].shipment_id}`);
        }
        if ((data.failed || []).length > 0) {
          Alert.error(`${data.failed.length} shipment(s) could not be loaded`);
        }
      };

      const applyStatus = (response) => {
        const { shipments, failed } = normalizeQrResolveResponse(response);
        if (!shipments.length) {
          throw new Error(response?.error || 'Shipment not found or access denied');
        }
        mergeResolvedShipments(shipments, failed);
        if (shipments.every((shipment) => shipment.notice_only)) {
          Alert.success(shipments[0].notice_message || 'Scan recorded. Status was not changed.');
        } else {
          Alert.success(`Scanned ${shipments[0].shipment_id}`);
        }
        if (failed.length > 0) {
          Alert.error(`${failed.length} shipment(s) could not be loaded`);
        }
      };

      if (targetMode === 'crossing') {
        try {
          const response = await shipmentCrossingService.resolveFromData({
            qrData,
            shipmentId: resolvedShipmentId,
          });
          applyCrossing(response);
        } catch (err) {
          if (!imageFile) throw err;
          const response = await shipmentCrossingService.resolveFromFile(imageFile);
          applyCrossing(response);
        }
        return;
      }

      try {
        const response = await shipmentQrStatusService.resolveFromQrData({
          qrData,
          shipmentId: resolvedShipmentId,
        });
        applyStatus(response);
      } catch (err) {
        if (!imageFile) throw err;
        const response = await shipmentQrStatusService.resolveFromQr(imageFile);
        applyStatus(response);
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || err.message || 'Could not resolve scanned QR');
    } finally {
      liveInFlightRef.current = false;
      setLiveResolving(false);
      setLiveScannerOpen(false);
    }
  };

  const handleUpdate = async () => {
    if (!resolvedShipments.length) {
      Alert.error('Scan a shipment QR first');
      return;
    }
    if (!selectedStatus) {
      Alert.error('Select a status');
      return;
    }
    if (!allowedStatuses.includes(selectedStatus)) {
      Alert.error('Selected status is not allowed for these shipments');
      return;
    }
    if (selectedStatus === 'Delivered' && user?.delivery_details_required !== false) {
      if (!String(deliveryReceiver || '').trim()) {
        Alert.error('Receiver name is required');
        return;
      }
      if (!String(deliveryContact || '').trim()) {
        Alert.error('Receiver contact is required');
        return;
      }
      if (!deliveryImage) {
        Alert.error('Delivery photo is required');
        return;
      }
      if (!deliveryGovIdImage) {
        Alert.error('Government ID proof is required');
        return;
      }
      const unpaid = eligibleShipments
        .filter((s) => requiresDeliveryCollection(s))
        .map((s) => s.shipment_id);
      if (unpaid.length > 0) {
        Alert.error(
          `Cannot mark as Delivered until payment is complete. Unpaid: ${unpaid.join(', ')}`,
        );
        return;
      }
    }

    if (eligibleShipments.length === 0) {
      Alert.error('No shipments are eligible for this status update');
      return;
    }
    if (selectedStatus === 'Dispatched' && !selectedMlSheetId) {
      Alert.error('Select today\'s ML sheet');
      return;
    }

    try {
      setUpdating(true);
      const shipmentIds = eligibleShipments.map((s) => s.shipment_id);
      if (selectedStatus === 'Dispatched') {
        const response = await shipmentQrStatusService.dispatchToMlSheet({
          shipmentIds,
          mlSheetId: selectedMlSheetId,
        });
        Alert.success(response?.message || 'Shipment dispatched');
        resetScanResults();
        if (onSuccess) onSuccess(response);
        return;
      }
      const payload = {
        shipment_ids: shipmentIds,
        status: selectedStatus,
      };
      if (remarks.trim()) payload.remarks = remarks.trim();
      if (selectedStatus === 'Delivered') {
        if (deliveryReceiver.trim()) payload.delivery_receiver = deliveryReceiver.trim();
        if (deliveryContact.trim()) payload.delivery_receiver_contact = deliveryContact.trim();
      }

      const response = await shipmentQrStatusService.updateStatusFromQr(payload, {
        delivery_receiver_image: deliveryImage,
        delivery_gov_id_image: deliveryGovIdImage,
      });

      if (isOtpRequiredResponse(response)) {
        setOtpPending(normalizeOtpPending(response));
        if (response.updated_count > 0) {
          Alert.success(response.message || 'OTP sent. Verify to complete remaining deliveries.');
        } else {
          Alert.success(response.message || 'OTP sent on WhatsApp');
        }
        return;
      }

      if (response.failed_count > 0) {
        const failedIds = (response.failed || []).map((f) => f.shipment_id).join(', ');
        Alert.error(response.message || `Some updates failed: ${failedIds}`);
      } else if (response.skipped_count > 0 && response.updated_count > 0) {
        Alert.success(response.message || 'Status updated successfully');
      } else if (response.skipped_count > 0 && !response.updated_count) {
        Alert.success(response.message || 'No changes needed');
      } else {
        Alert.success(response.message || 'Status updated successfully');
      }

      onSuccess?.(response);
      resetState();
      onClose?.();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const crossingNotices = crossingShipments.filter((s) => s.notice_only);
  const unloadCrossingShipments = crossingShipments.filter((s) => s.can_unload);
  const redispatchCrossingShipments = crossingShipments.filter((s) => s.can_redispatch);

  const handleUnloadCrossing = async () => {
    if (!crossingShipments.length) {
      Alert.error('Scan a shipment QR first');
      return;
    }
    if (unloadCrossingShipments.length === 0) {
      Alert.error('No scanned shipments are ready to unload (must be Dispatched)');
      return;
    }

    try {
      setCrossingSubmitting(true);
      const response = await shipmentCrossingService.unloadForCrossing({
        shipmentIds: unloadCrossingShipments.map((s) => s.shipment_id),
        remarks: remarks.trim() || undefined,
      });

      if (response.failed_count > 0) {
        Alert.error(response.message || 'Some unloads failed');
      } else {
        Alert.success(response.message || 'Shipments unloaded for crossing');
      }

      // Refresh previews so user can immediately pick vehicle and redispatch
      const refresh = await shipmentCrossingService.resolveFromData({
        shipmentIds: crossingShipments.map((s) => s.shipment_id),
      });
      const data = refresh?.data || {};
      setCrossingShipments(data.shipments || []);
      setCrossingFailed(data.failed || []);
      if (data.crossing_city) setCrossingHubCity(data.crossing_city);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to unload for crossing');
    } finally {
      setCrossingSubmitting(false);
    }
  };

  const handleCreateCrossing = async () => {
    if (!crossingShipments.length) {
      Alert.error('Scan a shipment QR first');
      return;
    }
    if (!crossingVehicle) {
      Alert.error('Select a vehicle for the crossing');
      return;
    }
    if (redispatchCrossingShipments.length === 0) {
      Alert.error('Unload the shipment first, then dispatch onto a vehicle');
      return;
    }

    try {
      setCrossingSubmitting(true);
      const response = await shipmentCrossingService.createCrossing({
        shipmentIds: redispatchCrossingShipments.map((s) => s.shipment_id),
        newVehicleId: crossingVehicle.id,
        remarks: remarks.trim() || undefined,
      });

      if (response.failed_count > 0) {
        Alert.error(response.message || 'Some crossings failed');
      } else {
        Alert.success(response.message || 'Crossing created successfully');
      }

      onSuccess?.(response);
      resetState();
      onClose?.();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create crossing');
    } finally {
      setCrossingSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={otpPending.length ? undefined : handleClose} aria-hidden="true" />
      <div
        className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="scan-qr-modal-title"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-start justify-between gap-3 z-10">
          <div>
            <h2 id="scan-qr-modal-title" className="text-lg font-bold text-gray-800 flex items-center gap-2">
              {mode === 'crossing' ? (
                <Shuffle className="w-5 h-5 text-orange-500" />
              ) : mode === 'attendance' ? (
                <CalendarCheck className="w-5 h-5 text-orange-500" />
              ) : (
                <QrCode className="w-5 h-5 text-orange-500" />
              )}
              {mode === 'crossing'
                ? 'Scan QR — Crossing'
                : mode === 'attendance'
                  ? 'Scan QR — Attendance'
                  : 'Scan QR — Update Status'}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {mode === 'attendance'
                ? 'Open camera, scan the printed hub QR, and keep GPS on.'
                : 'Open camera to auto-detect QR, or upload an image/PDF.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={updating || resolving || crossingSubmitting || attendanceSubmitting}
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label={t("common.close")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {[attendanceAllowed, statusAllowed, crossingAllowed].filter(Boolean).length > 1 && (
          <div className="px-4 sm:px-6 pt-3">
            <div className={`grid gap-1 p-1 bg-gray-100 rounded-lg ${
              [attendanceAllowed, statusAllowed, crossingAllowed].filter(Boolean).length === 3
                ? 'grid-cols-3'
                : 'grid-cols-2'
            }`}>
              {attendanceAllowed && (
                <button
                  type="button"
                  onClick={() => handleModeChange('attendance')}
                  className={`py-2 text-sm font-medium rounded-md transition-colors ${
                    mode === 'attendance' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Attendance
                </button>
              )}
              {statusAllowed && (
                <button
                  type="button"
                  onClick={() => handleModeChange('status')}
                  className={`py-2 text-sm font-medium rounded-md transition-colors ${
                    mode === 'status' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  Update Status
                </button>
              )}
              {crossingAllowed && (
                <button
                  type="button"
                  onClick={() => handleModeChange('crossing')}
                  className={`py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1.5 ${
                    mode === 'crossing' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  <Shuffle className="w-4 h-4" /> Crossing
                </button>
              )}
            </div>
          </div>
        )}

        <div className="p-4 sm:p-6 space-y-4">
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFileSelect(e, { fromCamera: true })}
          />
          <input
            ref={uploadRef}
            type="file"
            accept=".pdf,image/png,image/jpeg,image/jpg,image/gif,image/webp,.png,.jpg,.jpeg,.gif,.webp,application/pdf"
            className="hidden"
            onChange={(e) => handleFileSelect(e, { fromCamera: false })}
          />

          {mode === 'attendance' && (
            <p className="text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg p-3">
              Stand at the location hub, scan the printed attendance QR, and keep GPS on.
              You must be within 150 metres of the hub.
            </p>
          )}

          <div className={`grid gap-2 ${mode === 'attendance' ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <Button
              variant="primary"
              icon={Camera}
              fullWidth
              onClick={openCamera}
              disabled={resolving || updating || liveResolving || attendanceSubmitting}
            >
              {attendanceSubmitting ? 'Marking attendance…' : liveResolving ? 'Reading QR…' : 'Open Camera'}
            </Button>
            {mode !== 'attendance' && (
            <Button
              variant="secondary"
              icon={Upload}
              fullWidth
              onClick={() => uploadRef.current?.click()}
              disabled={resolving || updating}
            >
              Upload
            </Button>
            )}
          </div>

          {attendanceMessage && mode === 'attendance' && (
            <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
              {attendanceMessage}
            </p>
          )}

          {scanFile && (
            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
              {isPdfFile(scanFile) ? (
                <div className="flex items-center gap-3">
                  <FileText className="w-10 h-10 text-red-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{scanFile.name}</p>
                    <p className="text-xs text-gray-500">PDF — QR read from document pages</p>
                  </div>
                </div>
              ) : previewUrl ? (
                <img src={previewUrl} alt="QR scan preview" className="w-full max-h-40 object-contain rounded" />
              ) : null}
            </div>
          )}

          {resolving && (
            <div className="py-4">
              <Loader fullScreen={false} size="small" message="Reading QR from file..." />
            </div>
          )}

          {mode === 'status' && resolvedShipments.length > 0 && !resolving && (
            <div className="space-y-4 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-800">
                  {isBatch
                    ? `${resolvedShipments.length} shipment(s) found`
                    : 'Shipment details'}
                </p>
              </div>

              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                {resolvedShipments.map((shipment) => {
                  const eligibility = selectedStatus
                    ? getQrTransitionEligibility(shipment, selectedStatus)
                    : null;
                  const willUpdate = eligibility?.eligible;
                  const willSkip = eligibility && !eligibility.eligible;

                  return (
                  <div
                    key={shipment.shipment_id}
                    className={`p-3 text-sm ${willUpdate ? 'bg-green-50/80' : willSkip ? 'bg-gray-50' : 'bg-orange-50/50'}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <strong>{shipment.shipment_id}</strong>
                      <span className="capitalize text-gray-700">{shipment.status}</span>
                    </div>
                    <p className="text-gray-600 mt-1">
                      {shipment.sender_location} → {shipment.receiver_location}
                    </p>
                    <p className="mt-1">
                      <span className="text-gray-500">Payment:</span>{' '}
                      <span className={isCreditClientShipment(shipment) ? 'text-sky-800 font-medium' : isShipmentFullyPaid(shipment) ? 'text-green-700 font-medium' : 'text-red-600 font-medium'}>
                        {isCreditClientShipment(shipment)
                          ? 'Credit client'
                          : isShipmentFullyPaid(shipment)
                          ? 'Paid'
                          : `₹${Number(shipment.balance_amount || 0).toFixed(2)} due`}
                      </span>
                    </p>
                    {shipment.notice_only && (
                      <p className="text-xs text-amber-800 mt-1">{shipment.notice_message}</p>
                    )}
                    {selectedStatus && willSkip && !shipment.notice_only && (
                      <p className="text-xs text-amber-700 mt-1">{eligibility.reason}</p>
                    )}
                    {selectedStatus && willUpdate && (
                      <p className="text-xs text-green-700 mt-1">
                        {eligibility?.reapply
                          ? `Will confirm ${selectedStatus} again`
                          : `Will update to ${selectedStatus}`}
                      </p>
                    )}
                  </div>
                  );
                })}
              </div>

              {selectedStatus && skippedShipments.length > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  {skippedShipments.length} shipment(s) will be skipped for {selectedStatus}.
                  {eligibleShipments.length > 0
                    ? ` ${eligibleShipments.length} will be updated.`
                    : ' None eligible to update.'}
                </p>
              )}

              {resolveFailed.length > 0 && (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                  <p className="font-medium">Skipped from scan:</p>
                  {resolveFailed.map((item) => (
                    <p key={item.shipment_id}>
                      {item.shipment_id}: {item.reason}
                    </p>
                  ))}
                </div>
              )}

              {statusConfig.paymentMessage && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {statusConfig.paymentMessage}
                </p>
              )}

              {noticeShipments.length > 0 && actionableShipments.length === 0 ? (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {noticeShipments[0].notice_message || 'This scan was sent to the destination branch and admin. Status was not changed.'}
                </p>
              ) : allowedStatuses.length === 0 ? (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  This shipment cannot be updated from its current status.
                </p>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Status <span className="text-red-500">*</span>
                    </label>
                    <SelectField variant="form"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                    >
                      <option value="">Select status...</option>
                      {allowedStatuses.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </SelectField>
                    <p className="text-xs text-gray-500 mt-1">
                      Booked → Dispatch on today&apos;s ML sheet. Dispatched → Received. Received → confirm again or Delivered (if paid).
                    </p>
                  </div>

                  {selectedStatus === 'Dispatched' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Today&apos;s ML sheet <span className="text-red-500">*</span>
                      </label>
                      <SelectField variant="form"
                        value={selectedMlSheetId}
                        onChange={(e) => setSelectedMlSheetId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                        disabled={mlSheetsLoading}
                      >
                        <option value="">
                          {mlSheetsLoading ? 'Loading today\'s ML sheets...' : 'Select ML sheet or vehicle'}
                        </option>
                        {todayMlSheets.map((sheet) => {
                          const route = [sheet.from_location, sheet.to_location].filter(Boolean).join(' → ');
                          return (
                            <option key={sheet.ml_sheet_id} value={sheet.ml_sheet_id}>
                              {sheet.ml_sheet_id} — {sheet.vehicle_number || 'No vehicle'}
                              {route ? ` — ${route}` : ''}
                            </option>
                          );
                        })}
                      </SelectField>
                      <p className="text-xs text-gray-500 mt-1">
                        All open ML sheets created today. This list is not limited to the shipment route.
                      </p>
                      {!mlSheetsLoading && todayMlSheets.length === 0 && (
                        <p className="text-xs text-amber-700 mt-1">No open ML sheet was created today.</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      rows={2}
                      placeholder="Add remarks for this status update..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm resize-none"
                    />
                  </div>

                  {selectedStatus === 'Delivered' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-orange-50 rounded-xl border border-orange-200">
                      <div className="sm:col-span-2">
                        <h4 className="text-sm font-semibold text-orange-800">Delivery proof {user?.delivery_details_required === false ? '(optional)' : '(required)'}</h4>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Receiver Name</label>
                        <input
                          type="text"
                          value={deliveryReceiver}
                          onChange={(e) => setDeliveryReceiver(e.target.value)}
                          placeholder="Person who received the delivery"
                          maxLength={250}
                          className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Receiver Contact</label>
                        <input
                          type="text"
                          value={deliveryContact}
                          onChange={(e) => setDeliveryContact(e.target.value.replace(/[^\d+\-\s]/g, ''))}
                          placeholder="Contact number"
                          maxLength={30}
                          className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm"
                        />
                      </div>
                      <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <CapturePhotoField
                        label="Delivery photo"
                        required={user?.delivery_details_required !== false}
                        file={deliveryImage}
                        preview={deliveryImagePreview}
                        disabled={updating}
                        onPick={(file) => {
                          setDeliveryImage(file);
                          const reader = new FileReader();
                          reader.onloadend = () => setDeliveryImagePreview(reader.result);
                          reader.readAsDataURL(file);
                        }}
                        onClear={() => {
                          setDeliveryImage(null);
                          setDeliveryImagePreview(null);
                        }}
                      />
                      <CapturePhotoField
                        label="Government ID proof"
                        required={user?.delivery_details_required !== false}
                        file={deliveryGovIdImage}
                        preview={deliveryGovIdPreview}
                        disabled={updating}
                        onPick={(file) => {
                          setDeliveryGovIdImage(file);
                          const reader = new FileReader();
                          reader.onloadend = () => setDeliveryGovIdPreview(reader.result);
                          reader.readAsDataURL(file);
                        }}
                        onClear={() => {
                          setDeliveryGovIdImage(null);
                          setDeliveryGovIdPreview(null);
                        }}
                      />
                      </div>
                    </div>
                  )}

                  <Button
                    variant="primary"
                    icon={CheckCircle}
                    fullWidth
                    onClick={handleUpdate}
                    loading={updating}
                    disabled={updating || !selectedStatus || eligibleShipments.length === 0 || (selectedStatus === 'Dispatched' && !selectedMlSheetId)}
                  >
                    Update Status
                    {selectedStatus && eligibleShipments.length > 0
                      ? ` (${eligibleShipments.length})`
                      : ''}
                  </Button>
                </>
              )}
            </div>
          )}

          {mode === 'crossing' && crossingShipments.length > 0 && !resolving && (
            <div className="space-y-4 border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-800">
                  {crossingShipments.length > 1
                    ? `${crossingShipments.length} shipment(s) found`
                    : 'Shipment details'}
                </p>
                {crossingHubCity && (
                  <span className="text-xs text-gray-500">Crossing at: {crossingHubCity}</span>
                )}
              </div>

              <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                {crossingShipments.map((shipment) => (
                  <div
                    key={shipment.shipment_id}
                    className={`p-3 text-sm ${shipment.eligible ? 'bg-green-50/80' : 'bg-gray-50'}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <strong>{shipment.shipment_id}</strong>
                      <span className="capitalize text-gray-700">{shipment.status}</span>
                    </div>
                    <p className="text-gray-600 mt-1">
                      {shipment.sender_location} → {shipment.receiver_location}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Current vehicle: {shipment.current_vehicle_number || 'N/A'}
                      {shipment.ml_sheet_id ? ` · ML: ${shipment.ml_sheet_id}` : ''}
                    </p>
                    {shipment.can_unload && (
                      <p className="text-xs text-blue-700 mt-1">Ready to unload</p>
                    )}
                    {shipment.can_redispatch && (
                      <p className="text-xs text-green-700 mt-1">Unloaded — ready to dispatch</p>
                    )}
                    {shipment.notice_only && shipment.notice_message && (
                      <p className="text-xs text-amber-800 mt-1">{shipment.notice_message}</p>
                    )}
                    {!shipment.eligible && !shipment.notice_only && shipment.reason && (
                      <p className="text-xs text-amber-700 mt-1">{shipment.reason}</p>
                    )}
                  </div>
                ))}
              </div>

              {crossingFailed.length > 0 && (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
                  <p className="font-medium">Skipped from scan:</p>
                  {crossingFailed.map((item) => (
                    <p key={item.shipment_id}>
                      {item.shipment_id}: {item.reason}
                    </p>
                  ))}
                </div>
              )}

              {crossingNotices.length > 0 && crossingNotices.length === crossingShipments.length ? (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  {crossingNotices[0].notice_message || 'This scan was sent to the destination branch and admin. Crossing was not changed.'}
                </p>
              ) : (
              <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
                <textarea
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  rows={2}
                  placeholder="Add a note for unload or crossing..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-sm text-sm resize-none"
                />
              </div>

              <Button
                variant="secondary"
                fullWidth
                onClick={handleUnloadCrossing}
                loading={crossingSubmitting}
                disabled={crossingSubmitting || unloadCrossingShipments.length === 0}
              >
                1. Unload
                {unloadCrossingShipments.length > 0 ? ` (${unloadCrossingShipments.length})` : ''}
              </Button>

              <CrossingVehicleSelect
                value={crossingVehicle}
                onChange={setCrossingVehicle}
                disabled={crossingSubmitting}
              />

              <Button
                variant="primary"
                icon={Shuffle}
                fullWidth
                onClick={handleCreateCrossing}
                loading={crossingSubmitting}
                disabled={
                  crossingSubmitting
                  || !crossingVehicle
                  || redispatchCrossingShipments.length === 0
                }
              >
                2. Dispatch / Cross
                {redispatchCrossingShipments.length > 0
                  ? ` (${redispatchCrossingShipments.length})`
                  : ''}
              </Button>

              <p className="text-xs text-gray-400">
                Unload first, then dispatch onto a vehicle. Hub Admin or Agent at the designated
                crossing hub can complete both steps. No labour or remittance changes.
              </p>
              </>
              )}
            </div>
          )}
        </div>
      </div>

      <LiveQrScanner
        open={liveScannerOpen}
        onClose={() => setLiveScannerOpen(false)}
        onScan={handleLiveQrScan}
        busy={liveResolving || attendanceSubmitting}
        continuous={false}
        title={mode === 'attendance' ? 'Scan Attendance QR' : 'Scan Shipment QR'}
        hint={
          mode === 'attendance'
            ? 'Hold the printed hub QR in the frame. Stay within 150m of the hub.'
            : 'Hold the label in the frame. The scanner closes after the QR is read.'
        }
      />
      <DeliveryOtpModal
        open={otpPending.length > 0}
        pending={otpPending}
        onClose={() => setOtpPending([])}
        onVerified={() => {
          setOtpPending([]);
          Alert.success('Delivery verified and completed');
          onSuccess?.({ success: true, otp_verified: true });
          resetState();
          onClose?.();
        }}
      />
    </div>
  );
};

export default ScanQrUpdateStatusModal;
